import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListResponseContract, Test, TestContract } from '@isophi/teachers-shared';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { TimestampObservableCache } from '../utils/timestamp-observable-cache';

@Injectable({
  providedIn: 'root',
})
export class TestService {
  protected static readonly CACHE_EXPIRATION: number = 3600; // in seconds

  protected testsCache: TimestampObservableCache<Test[]> | null = null;

  protected testCache: { [key: string]: TimestampObservableCache<Test> } = {};

  public constructor(private http: HttpClient) {}

  public get tests$(): Observable<Test[]> {
    const extraFields = 'color,short_title';
    const url = `${environment.teacherAPI}/tests/?extra_fields=${extraFields}`;
    const publicUrl = `${environment.teacherAPI}/public-tests/?extra_fields=${extraFields}`;

    if (this.testsCache === null || this.testsCache.isExpired()) {
      const tests$ = forkJoin([
        this.http.get<ListResponseContract<TestContract>>(url),
        this.http.get<ListResponseContract<TestContract>>(publicUrl),
      ]).pipe(
        map(([testData, publicTestData]) => ({ ...testData, ...publicTestData })),
        map((response) => response.results.map((testData) => Test.deserialize(testData))),
        shareReplay(1),
        catchError((error) => {
          this.testsCache = null;
          return throwError(error);
        })
      );

      this.testsCache = new TimestampObservableCache<Test[]>(tests$, TestService.CACHE_EXPIRATION);
    }

    return this.testsCache.observable;
  }

  public getTest(uuid: string): Observable<Test> {
    const extraFields = 'color,short_title';
    const url = `${environment.teacherAPI}/tests/${uuid}/?extra_fields=${extraFields}`;
    const publicUrl = `${environment.teacherAPI}/public-tests/${uuid}/?extra_fields=${extraFields}`;
    let testWithoutCache;

    const testCache = this.testCache ? this.testCache[uuid] : undefined;
    if (testCache === undefined || testCache.isExpired()) {
      const test$ = this.http.get<TestContract>(url).pipe(
        map((testData) => Test.deserialize(testData)),
        shareReplay(1),
        catchError((error) => {
          if (error.status === 404) {
            return this.http.get<TestContract>(publicUrl).pipe(
              map((testData) => Test.deserialize(testData)),
              shareReplay(1),
              catchError((err) => {
                delete this.testCache[uuid];
                return throwError(err);
              })
            );
          } else {
            delete this.testCache[uuid];
            return throwError(error);
          }
        })
      );

      if (this.testCache) {
        this.testCache[uuid] = new TimestampObservableCache<Test>(test$, TestService.CACHE_EXPIRATION);
      } else {
        testWithoutCache = test$;
      }
    }

    if (this.testCache && this.testCache[uuid]) {
      return this.testCache[uuid].observable
    } else {
      return testWithoutCache
    }
  }

  /**
   * Invalidate groups cache.
   */
  public invalidateCache(): void {
    this.testsCache = null;
    this.testCache = null;
  }
}
